import {
  Flex,
  View,
  Text,
  Button,
  Form,
  ProgressCircle,
  Heading,
  Divider,
  Picker,
  Item
} from '@adobe/react-spectrum';
import React, { useState, useEffect, useRef } from 'react';
import { useRsdForms } from '../store';
import styles from './CreateMoment.module.scss';
import { FormContent } from './FormContent';
import { MOCK_FORM_FIELDS } from '../store/formFields';
import { buildMomentPayload } from '../utils/payloadBuilder';
import {
  END_OF_LIFE_MOMENT_TYPE,
  omitEolDisallowedPayloadKeys
} from '../utils/eolCreatePayload';
import { api } from '../store/api';
import { AlertBanner } from '../../Modal/AlertBanner';
import addStyles from '../../Add.module.css';
import {
  clearEventsCache,
  clearParentMomentsCache
} from '../../../services/momentsApi';
import { useSlideoutUrl } from '../../../hooks/useSlideoutUrl';
import closeIcon from '../../../../assets/icons/close-20.svg';
import {
  PHASE_FIELDS,
  PHASES_WITHOUT_INTERNAL_BETA,
  INTERNAL_BETA_PHASE
} from './utils/phaseFields';
import { useEolStagesValidation } from './hooks/useEolStagesValidation';
import { applyEolStagePayloadKeyNames } from './utils/eolStagesPayload';
import {
  FIELD_VALIDATION_ERROR_ATTR,
  removeFieldErrorBorder,
  setFieldErrorBorder
} from './utils/eolStagesValidation';
import { useSelector } from 'react-redux';

export interface CreateMomentProps {
  onClose: () => void;
  momentTypeId: string | null;
  onSuccess?: (message: string) => void;
}

interface MomentData {
  name?: string;
  description?: string;
  [key: string]: any;
}

export const CreateMoment: React.FC<CreateMomentProps> = ({
  onClose,
  momentTypeId,
  onSuccess
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Get slideout navigation hook
  const { openSlideout } = useSlideoutUrl();

  // Initialize the form values with the momentTypeId already selected
  const [formValues, setFormValues] = useState<Record<string, any>>(() => ({
    momentType: momentTypeId || '',
    typeOfRelease: momentTypeId || '' // Always default to the selected moment type
  }));

  // Track the current active moment type
  const [activeMomentType, setActiveMomentType] = useState<string | null>(
    momentTypeId
  );
  // Get the actual moment type name from config
  // const showCloseButton = !(
  //   activeMomentType === 'Event' || activeMomentType === 'Key Date'
  // );
  const {
    formFields,
    loadingFormFields,
    formFieldsError,
    createMoment,
    loadingMoments,
    //momentTypes,
    selectMomentType,
    allFormConfigs,
    selectedMomentType,
    clearFormFieldsCacheEntry,
    loadFormFields
  } = useRsdForms();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [momentTypes, setMomentTypes] = useState<any[]>([]);
  const [loadingMomentTypes, setLoadingMomentTypes] = useState(false);
  const [momentTypesError, setMomentTypesError] = useState<string | null>(null);

  const [alert, setAlert] = useState<{
    message: string;
    variant: 'neutral' | 'informative' | 'error';
  } | null>(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false); // Track if cache was already cleared

  const isRepeatableGroupComponentField = (field: any): boolean =>
    Boolean(field && field.type === 'repeatable_group_component');

  const {
    eolStageGroups,
    validateMinOneStage,
    syncEolStageDomErrors,
    changeAffectsEolValidation
  } = useEolStagesValidation(formFields, activeMomentType);

  // Update form values when momentTypeId changes
  useEffect(() => {
    if (momentTypeId) {
      // Update the momentType field in formValues when momentTypeId changes
      setFormValues(prev => ({
        ...prev,
        momentType: momentTypeId
      }));
      setActiveMomentType(momentTypeId);
      // Reset submit/error state and clear DOM errors when type changes externally
      setHasSubmitted(false);
      setValidationErrors({});
      clearAllErrorMessages();
    }
  }, [momentTypeId]);

  // Sync local activeMomentType with Redux selectedMomentType
  // This ensures the form works even if Redux state changes
  useEffect(() => {
    if (selectedMomentType && selectedMomentType !== activeMomentType) {
      setActiveMomentType(selectedMomentType);
      setFormValues(prev => ({
        ...prev,
        momentType: selectedMomentType
      }));
      // Reset submit/error state and clear DOM errors when Redux sync changes type
      setHasSubmitted(false);
      setValidationErrors({});
      clearAllErrorMessages();
    }
  }, [selectedMomentType, activeMomentType]);

  // Ensure form fields are loaded for the active moment type
  // This fixes issues where form fields might not load properly
  useEffect(() => {
    if (
      activeMomentType &&
      formFields.length === 0 &&
      !loadingFormFields &&
      !formFieldsError
    ) {
      // Clear cache and reload form fields to fix inconsistent state
      clearFormFieldsCacheEntry(activeMomentType);
      loadFormFields(activeMomentType);
    }
  }, [
    activeMomentType,
    formFields.length,
    loadingFormFields,
    formFieldsError,
    clearFormFieldsCacheEntry,
    loadFormFields
  ]);

  // Get the name of the selected moment type
  const momentTypeName = activeMomentType
    ? momentTypes.find(type => type.id === activeMomentType)?.name || 'Moment'
    : 'Moment';

  // Get the dialog size from the real form config in Redux
  const getDialogSizeClass = () => {
    if (
      !activeMomentType ||
      !allFormConfigs ||
      !allFormConfigs[activeMomentType]
    )
      return '';
    const formConfig = allFormConfigs[activeMomentType];
    switch (formConfig.dialogSize) {
      case 'small':
        return styles.smallDialog;
      case 'medium':
        return styles.mediumDialog;
      case 'large':
        return styles.largeDialog;
      default:
        return '';
    }
  };

  // ----------------------
  // Helper utilities (no UI/logic change, just deduplication)
  // ----------------------
  const addRequiredErrorMessage = (parentEl: Element | null) => {
    if (!parentEl) return;
    // Skip hidden elements (showField: false renders them with display:none or visibility:hidden)
    const computedStyle = window.getComputedStyle(parentEl);
    if (
      computedStyle.display === 'none' ||
      computedStyle.visibility === 'hidden'
    ) {
      return;
    }
    const existingError = parentEl.querySelector('.error-message');
    if (!existingError) {
      const msg = document.createElement('div');
      msg.textContent = 'Required Field';
      msg.style.color = '#d31510';
      msg.style.marginTop = '4px';
      msg.style.fontSize = '12px';
      msg.className = 'error-message';
      parentEl.appendChild(msg);
    }
  };

  const removeExistingErrorMessage = (parentEl: Element | null) => {
    if (!parentEl) return;
    const existingError = parentEl.querySelector('.error-message');
    if (existingError) existingError.remove();
  };

  const removeFieldErrorMessage = (fieldName: string) => {
    const errorElement = document.querySelector(
      `[data-field-name="${fieldName}"]`
    );
    if (errorElement) {
      removeExistingErrorMessage(errorElement);
    }
  };

  const isPhaseDatePresent = (dateVal: any) => {
    if (!dateVal) return false;
    if (typeof dateVal === 'string')
      return dateVal.trim() !== '' && dateVal !== 'N/A';
    if (typeof dateVal === 'object') {
      if (dateVal.type === 'special') return dateVal.value === 'TBD';
      if (dateVal.type === 'date') return Boolean(dateVal.date);
      if (dateVal.type === 'months')
        return Array.isArray(dateVal.months) && dateVal.months.length > 0;
      if (dateVal.type === 'quarters')
        return Array.isArray(dateVal.quarters) && dateVal.quarters.length > 0;
    }
    return false;
  };

  /**
   * Validates phases according to new rules:
   * - If Internal Beta is N/A/empty: Need at least 1 phase from (Private Beta, Public Beta, GA)
   * - If Internal Beta is filled: Need at least 2 phases total (Internal Beta + at least one other)
   * Returns { isValid: boolean, missingPhases: string[] }
   */
  const validatePhases = (
    phaseGroupNames: string[],
    values: Record<string, any>
  ): { isValid: boolean; missingPhases: string[] } => {
    const hasInternalBeta = isPhaseDatePresent(
      values?.[INTERNAL_BETA_PHASE]?.phaseDate
    );

    // Count phases present
    const internalBetaPresent = hasInternalBeta;
    const otherPhasesPresent = PHASES_WITHOUT_INTERNAL_BETA.filter(phaseName =>
      phaseGroupNames.includes(phaseName)
    ).filter(phaseName => isPhaseDatePresent(values?.[phaseName]?.phaseDate));

    let isValid = false;
    const missingPhases: string[] = [];

    if (internalBetaPresent) {
      // Scenario 2: Internal Beta is filled
      // Need at least 1 additional phase from Private/Public/GA
      if (otherPhasesPresent.length >= 1) {
        isValid = true;
      } else {
        // Missing at least one of the other phases - mark ALL of them as missing
        PHASES_WITHOUT_INTERNAL_BETA.filter(phaseName =>
          phaseGroupNames.includes(phaseName)
        ).forEach(phaseName => {
          if (!isPhaseDatePresent(values?.[phaseName]?.phaseDate)) {
            missingPhases.push(phaseName);
          }
        });
      }
    } else {
      // Scenario 1: Internal Beta is N/A or empty
      // Need at least 1 phase from Private/Public/GA
      if (otherPhasesPresent.length >= 1) {
        isValid = true;
      } else {
        // Missing at least one of the other phases - mark ALL of them as missing
        PHASES_WITHOUT_INTERNAL_BETA.filter(phaseName =>
          phaseGroupNames.includes(phaseName)
        ).forEach(phaseName => {
          if (!isPhaseDatePresent(values?.[phaseName]?.phaseDate)) {
            missingPhases.push(phaseName);
          }
        });
      }
    }

    return { isValid, missingPhases };
  };

  const addCustomErrorMessage = (parentEl: Element | null, message: string) => {
    if (!parentEl) return;
    const existingError = parentEl.querySelector('.error-message');
    if (!existingError) {
      const msg = document.createElement('div');
      msg.textContent = message;
      msg.style.color = '#d31510';
      msg.style.marginTop = '4px';
      msg.style.fontSize = '12px';
      msg.className = 'error-message';
      parentEl.appendChild(msg);
    } else {
      // Update existing error message
      existingError.textContent = message;
    }
  };

  const updatePhaseDomErrors = (
    phaseGroupNames: string[],
    values: Record<string, any>,
    showErrors: boolean
  ) => {
    // Use new validation logic
    const { isValid, missingPhases } = validatePhases(phaseGroupNames, values);

    // Check if only Internal Beta is selected
    const hasInternalBeta = isPhaseDatePresent(
      values?.[INTERNAL_BETA_PHASE]?.phaseDate
    );
    const otherPhasesPresent = PHASES_WITHOUT_INTERNAL_BETA.filter(phaseName =>
      phaseGroupNames.includes(phaseName)
    ).filter(phaseName => isPhaseDatePresent(values?.[phaseName]?.phaseDate));

    const onlyInternalBetaSelected =
      hasInternalBeta && otherPhasesPresent.length === 0;

    phaseGroupNames.forEach(gn => {
      const groupEl = document.querySelector(`[data-field-name="${gn}"]`);
      if (!groupEl) return;

      const hasDate = isPhaseDatePresent(values?.[gn]?.phaseDate);
      const tierVal = values?.[gn]?.tier;
      const isTierEmpty =
        tierVal === undefined ||
        tierVal === null ||
        (typeof tierVal === 'string' && tierVal.trim() === '');

      const dateFieldEl = groupEl.querySelector(
        '[data-field-name="phaseDate"]'
      );
      if (dateFieldEl) {
        // THREE STATES: add error, remove error, or keep existing state
        const isInMissingPhases = missingPhases.includes(gn);
        const existingError = dateFieldEl.querySelector('.error-message');

        // Special case: Show custom error for Internal Beta when it's the only one selected
        if (
          showErrors &&
          onlyInternalBetaSelected &&
          gn === INTERNAL_BETA_PHASE
        ) {
          addCustomErrorMessage(
            dateFieldEl,
            'At least one additional/subsequent phase must be present when adding an Internal Beta release.'
          );
        }
        // State 1: Phase needs error (is required but not filled and we're showing errors)
        else if (showErrors && !isValid && isInMissingPhases) {
          addRequiredErrorMessage(dateFieldEl);
        }
        // State 2: Phase is definitely valid - remove any error
        // CRITICAL FIX: Only remove if this specific phase is filled OR validation passed
        // Do NOT remove just because showErrors is false - that would clear errors during typing
        else if (hasDate || isValid) {
          removeExistingErrorMessage(dateFieldEl);
        }
        // State 3: Keep existing state (don't add or remove)
        // This includes when showErrors=false (before submission) - we don't clear existing errors
        else {
        }
      }

      const tierFieldEl = groupEl.querySelector('[data-field-name="tier"]');
      if (tierFieldEl) {
        // Tier is required if date is present (keeping same logic)
        if (hasDate && isTierEmpty && showErrors) {
          addRequiredErrorMessage(tierFieldEl);
        } else {
          removeExistingErrorMessage(tierFieldEl);
        }
      }
    });
  };

  const clearAllErrorMessages = () => {
    try {
      const container = document.querySelector(`.${styles.formContainer}`);
      const errors = container
        ? container.querySelectorAll('.error-message')
        : document.querySelectorAll('.error-message');
      errors.forEach(err => err.remove());
      const errorFields = container
        ? container.querySelectorAll(`[${FIELD_VALIDATION_ERROR_ATTR}]`)
        : document.querySelectorAll(`[${FIELD_VALIDATION_ERROR_ATTR}]`);
      errorFields.forEach(el =>
        el.removeAttribute(FIELD_VALIDATION_ERROR_ATTR)
      );
    } catch (e) {
      // ignore DOM cleanup failures
    }
  };

  const eolStageDomHelpers = {
    addRequiredErrorMessage,
    removeExistingErrorMessage,
    setFieldErrorBorder,
    removeFieldErrorBorder
  };

  // Handle blur events for form fields
  const handleInputBlur = (name: string) => {
    // No special validation needed for name field anymore
  };

  const handleInputChange = (name: string, value: any) => {
    // No special validation for name field - users can type anything

    setFormValues(prev => {
      // For custom_component fields, merge the incoming partial value with existing group
      const fieldConfig = formFields.find(f => f.name === name);
      if (fieldConfig && fieldConfig.type === 'custom_component') {
        const existingGroup = prev[name] || {};
        const updatedGroup = {
          ...existingGroup,
          ...value
        };
        return {
          ...prev,
          [name]: updatedGroup
        };
      }

      // For all other fields, replace the value
      return {
        ...prev,
        [name]: value,
        // If the momentType changes, also update typeOfRelease
        ...(name === 'momentType' ? { typeOfRelease: value } : {})
      };
    });

    // Special handling for LDAP fields inside required sections (Primary Contacts)
    try {
      const isLdapField =
        formFields.find(f => f.name === name)?.type === 'ldap';
      if (isLdapField) {
        const updatedValues = { ...formValues, [name]: value };
        // Collect all ldap fields in sections marked required
        const ldapFieldsInRequiredSections = formFields.filter(
          f => f.type === 'ldap' && (f.sectionConfig as any)?.required
        );
        const anySelected = ldapFieldsInRequiredSections.some(
          f =>
            Array.isArray(updatedValues[f.name]) &&
            updatedValues[f.name]?.length > 0
        );
        if (anySelected) {
          // Clear error messages from all ldap fields in this section
          ldapFieldsInRequiredSections.forEach(f =>
            removeFieldErrorMessage(f.name)
          );
        } else if (hasSubmitted) {
          // If none selected and user already submitted, ensure errors remain visible for all ldap fields
          ldapFieldsInRequiredSections.forEach(f => {
            const el = document.querySelector(`[data-field-name="${f.name}"]`);
            if (el) addRequiredErrorMessage(el);
          });
        }
      }
    } catch (e) {
      // ignore
    }

    // If user has submitted already, and this field becomes valid, remove its DOM error message
    try {
      const fieldConfig = formFields.find(f => f.name === name);
      if (hasSubmitted && fieldConfig && fieldConfig.required) {
        const nowValid = !isFieldEmpty(fieldConfig, value, {
          ...formValues,
          [name]: value
        });
        if (nowValid) {
          removeFieldErrorMessage(name);
        }
      }
    } catch (e) {
      // Non-blocking: avoid throwing if DOM nodes not found
    }

    // Special validation handling for Phases (group_component):
    // - At least ONE group's phaseDate must be set (not empty/N/A)
    // - If a group's phaseDate is set, its tier is required
    try {
      const phaseGroups = formFields.filter(
        f =>
          f.type === 'group_component' &&
          Array.isArray((f as any).fieldGroup) &&
          (f as any).fieldGroup.some((gf: any) => gf.name === 'phaseDate')
      );
      const phaseGroupNames = phaseGroups.map(pg => pg.name);

      if (phaseGroupNames.includes(name)) {
        const updatedValues = { ...formValues, [name]: value };
        updatePhaseDomErrors(phaseGroupNames, updatedValues, hasSubmitted);

        // Check if tier changed and update audienceStrategyMappings validation
        if (
          hasSubmitted &&
          value &&
          typeof value === 'object' &&
          'tier' in value
        ) {
          const hasL1orL2 = PHASE_FIELDS.some(phaseName => {
            const phaseData = updatedValues[phaseName];
            if (phaseData && typeof phaseData === 'object') {
              const tier = phaseData.tier;
              return tier === 'L1' || tier === 'L2';
            }
            return false;
          });

          const audienceStrategyMappingsEl = document.querySelector(
            '[data-field-name="audienceStrategyMappings"]'
          );
          if (audienceStrategyMappingsEl) {
            const audienceStrategyMappingsField = formFields.find(
              f => f.name === 'audienceStrategyMappings'
            );
            const audienceStrategyMappingsValue =
              updatedValues.audienceStrategyMappings;
            const isEmpty =
              !Array.isArray(audienceStrategyMappingsValue) ||
              audienceStrategyMappingsValue.length === 0;

            if (
              activeMomentType !== END_OF_LIFE_MOMENT_TYPE &&
              hasL1orL2 &&
              isEmpty
            ) {
              // L1/L2 exists and audience strategy mappings is empty - show error
              removeExistingErrorMessage(audienceStrategyMappingsEl);
              addRequiredErrorMessage(audienceStrategyMappingsEl);
            } else {
              // EOL, no L1/L2, or audience strategy mappings is filled - remove error
              removeExistingErrorMessage(audienceStrategyMappingsEl);
            }
          }
        }
      }
    } catch (e) {
      // ignore
    }

    try {
      if (
        activeMomentType === END_OF_LIFE_MOMENT_TYPE &&
        changeAffectsEolValidation(name)
      ) {
        syncEolStageDomErrors(
          { ...formValues, [name]: value } as Record<string, unknown>,
          hasSubmitted,
          isPhaseDatePresent,
          eolStageDomHelpers
        );
      }
    } catch (e) {
      // ignore
    }

    // Custom component: effectiveDate must be anything other than N/A when required
    try {
      if (name === 'releaseDateInfo') {
        // Merge partial value with existing group for validation
        const existingGroup = formValues?.releaseDateInfo || {};
        const mergedGroup = { ...existingGroup, ...value };
        const updatedValues = { ...formValues, [name]: mergedGroup };
        const group = updatedValues?.releaseDateInfo || {};
        const eff = group?.effectiveDate;
        const effValid = isPhaseDatePresent(eff); // same semantics: any non-N/A date/month/quarter/TBD
        const groupEl = document.querySelector(
          '[data-field-name="effectiveDate"]'
        );
        if (groupEl) {
          if (!effValid && hasSubmitted) {
            addRequiredErrorMessage(groupEl as Element);
          } else {
            removeExistingErrorMessage(groupEl as Element);
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // Marketing date (top-level): must be anything other than N/A when required
    try {
      if (name === 'marketingDate') {
        const updatedValues = { ...formValues, marketingDate: value };
        const mkd = updatedValues?.marketingDate;
        const mkdValid = isPhaseDatePresent(mkd);
        const mkdEl = document.querySelector(
          '[data-field-name="marketingDate"]'
        );
        if (mkdEl) {
          if (!mkdValid && hasSubmitted) {
            addRequiredErrorMessage(mkdEl as Element);
          } else {
            removeExistingErrorMessage(mkdEl as Element);
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // RTM phases live-validation: clear section error when a valid phase is added
    try {
      const rtmField = formFields.find(isRepeatableGroupComponentField);
      if (rtmField && name === rtmField.name && hasSubmitted) {
        const rtmEl = document.querySelector(
          `[data-field-name="${rtmField.name}"]`
        );
        const phases = Array.isArray(value) ? value : [];
        const hasValidPhase = phases.some((p: any) => p && p.routeToMarket);
        if (rtmEl) {
          if (hasValidPhase) {
            removeExistingErrorMessage(rtmEl);
          } else {
            addRequiredErrorMessage(rtmEl);
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // Handle top-level tier field changes (for moment types like "Marketing Moment")
    try {
      if (name === 'tier' && hasSubmitted) {
        const updatedValues: Record<string, any> = {
          ...formValues,
          [name]: value
        };

        // Check if L1/L2 is now selected (top-level or any phase)
        const hasL1orL2 =
          value === 'L1' ||
          value === 'L2' ||
          PHASE_FIELDS.some(phaseName => {
            const phaseData = updatedValues[phaseName];
            return (
              phaseData &&
              typeof phaseData === 'object' &&
              (phaseData.tier === 'L1' || phaseData.tier === 'L2')
            );
          });

        // Update audienceStrategyMappings validation state
        const audienceStrategyMappingsEl = document.querySelector(
          '[data-field-name="audienceStrategyMappings"]'
        );
        if (audienceStrategyMappingsEl) {
          const audienceStrategyMappingsValue =
            updatedValues.audienceStrategyMappings;
          const isEmpty =
            !Array.isArray(audienceStrategyMappingsValue) ||
            audienceStrategyMappingsValue.length === 0;

          if (
            activeMomentType !== END_OF_LIFE_MOMENT_TYPE &&
            hasL1orL2 &&
            isEmpty
          ) {
            // L1/L2 exists and audience strategy mappings is empty - show error
            removeExistingErrorMessage(audienceStrategyMappingsEl);
            addRequiredErrorMessage(audienceStrategyMappingsEl);
          } else {
            // EOL, no L1/L2, or audience strategy mappings is filled - remove error
            removeExistingErrorMessage(audienceStrategyMappingsEl);
          }
        }
      }
    } catch (e) {
      // ignore
    }

    // If the moment type field is changed, update the active moment type
    if (name === 'momentType' && value !== activeMomentType) {
      // Update Redux with the new selected moment type
      selectMomentType(value);

      // Update the active moment type to trigger form fields loading
      setActiveMomentType(value);

      // Preserve all existing field values when changing moment type
      setFormValues(prev => {
        const preservedValues = {
          momentType: value,
          // Preserve all existing field values
          ...Object.entries(prev).reduce((acc, [key, val]) => {
            // Skip momentType since we're setting it explicitly
            if (key !== 'momentType') {
              acc[key] = val;
            }
            return acc;
          }, {} as Record<string, any>)
        };
        return preservedValues;
      });
      // Reset submit state and clear all existing error messages
      setHasSubmitted(false);
      setValidationErrors({});
      clearAllErrorMessages();
    }
    // Run validation on every change (but only show errors if hasSubmitted)
    validateForm({ ...formValues, [name]: value });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setValidationErrors({});
    setAlert(null);
    setHasSubmitted(true); // Mark as submitted
    let isFormValid = validateForm(formValues);

    // Phases validation (only when phase groups exist):
    // - If Internal Beta is N/A/empty: Need at least 1 phase from (Private Beta, Public Beta, GA)
    // - If Internal Beta is filled: Need at least 2 phases total (Internal Beta + at least one other)
    // - If a group's date is set, its tier is required
    try {
      const phaseGroups = formFields.filter(
        f =>
          f.type === 'group_component' &&
          Array.isArray((f as any).fieldGroup) &&
          (f as any).fieldGroup.some((gf: any) => gf.name === 'phaseDate')
      );
      const phaseGroupNames = phaseGroups.map(pg => pg.name);

      // Only enforce when such groups are present in this form config
      if (phaseGroupNames.length > 0) {
        // Use new validation logic
        const { isValid: phasesValid } = validatePhases(
          phaseGroupNames,
          formValues
        );

        // Check for missing tiers (keeping same logic)
        const groupsWithDateMissingTier: string[] = phaseGroupNames.filter(
          gn => {
            const hasDate = isPhaseDatePresent(formValues?.[gn]?.phaseDate);
            if (!hasDate) return false;
            const tierVal = formValues?.[gn]?.tier;
            const isTierEmpty =
              tierVal === undefined ||
              tierVal === null ||
              (typeof tierVal === 'string' && tierVal.trim() === '');
            return isTierEmpty;
          }
        );

        if (!phasesValid || groupsWithDateMissingTier.length > 0) {
          isFormValid = false;
        }

        updatePhaseDomErrors(phaseGroupNames, formValues, true);
      }
    } catch (e) {
      // ignore phase validation errors
    }

    // Validate effectiveDate within custom component (releaseDateInfo)
    try {
      const releaseInfo = formValues?.releaseDateInfo;
      const effValid = isPhaseDatePresent(releaseInfo?.effectiveDate);
      const effEl = document.querySelector('[data-field-name="effectiveDate"]');
      if (effEl) {
        if (!effValid) {
          addRequiredErrorMessage(effEl as Element);
          isFormValid = false;
        } else {
          removeExistingErrorMessage(effEl as Element);
        }
      }
    } catch (e) {
      // ignore
    }

    // Validate marketingDate (top-level)
    try {
      const mkdValid = isPhaseDatePresent(formValues?.marketingDate);
      const mkdEl = document.querySelector('[data-field-name="marketingDate"]');
      if (mkdEl) {
        if (!mkdValid) {
          addRequiredErrorMessage(mkdEl as Element);
          isFormValid = false;
        } else {
          removeExistingErrorMessage(mkdEl as Element);
        }
      }
    } catch (e) {
      // ignore
    }

    // Validate repeatable_group_component (RTM): routeToMarket + customerNotificationDate
    try {
      const rtmField = formFields.find(isRepeatableGroupComponentField);
      if (rtmField) {
        const rtmValue = formValues[rtmField.name];
        const rtmEl = document.querySelector(
          `[data-field-name="${rtmField.name}"]`
        );
        const phases = Array.isArray(rtmValue) ? rtmValue : [];
        const hasValidPhase = phases.some((p: any) => p && p.routeToMarket);
        const hasIncompletePhase = phases.some(
          (p: any) =>
            p &&
            p.routeToMarket &&
            (!p.customerNotificationDate ||
              (typeof p.customerNotificationDate === 'object' &&
                p.customerNotificationDate.type === 'special' &&
                p.customerNotificationDate.value === 'N/A'))
        );

        if (!hasValidPhase || hasIncompletePhase) {
          isFormValid = false;
          if (rtmEl) {
            removeExistingErrorMessage(rtmEl);
            if (!hasValidPhase) {
              addRequiredErrorMessage(rtmEl);
            }
          }
        } else if (rtmEl) {
          removeExistingErrorMessage(rtmEl);
        }
      }
    } catch (e) {
      // ignore
    }

    if (!isFormValid) {
      // Helper to check if ANY tier (top-level or phase-level) is L1 or L2
      const hasL1orL2Tier = (): boolean => {
        // Check top-level tier field (for moment types like "Marketing Moment")
        const topLevelTier = formValues.tier;
        if (topLevelTier === 'L1' || topLevelTier === 'L2') {
          return true;
        }

        // Check phase-level tier fields
        return PHASE_FIELDS.some(phaseName => {
          const phaseData = formValues[phaseName];
          if (phaseData && typeof phaseData === 'object') {
            const tier = phaseData.tier;
            return tier === 'L1' || tier === 'L2';
          }
          return false;
        });
      };

      // Find ALL fields with errors using the same logic as validation
      const allErrorFields = formFields.filter(field => {
        if (!field.required) return false;
        // Skip fields effectively disabled due to missing dependencies
        const dependencies = Array.isArray(field.dependsOn)
          ? field.dependsOn
          : field.dependsOn
          ? [field.dependsOn]
          : [];
        const isEmptyDep = (v: any) =>
          v === undefined ||
          v === null ||
          (typeof v === 'string' && v.trim() === '') ||
          (Array.isArray(v) && v.length === 0);
        const missingDep = dependencies.some(dep =>
          isEmptyDep(formValues[dep])
        );
        if (missingDep) return false;

        // Special handling for audienceStrategyMappings - skip if no L1/L2 tier or EOL
        if (
          field.name === 'audienceStrategyMappings' &&
          (formValues.momentType === END_OF_LIFE_MOMENT_TYPE ||
            !hasL1orL2Tier())
        ) {
          return false;
        }

        return isFieldEmpty(field, formValues[field.name], formValues);
      });

      // Show error message for ALL empty required fields
      // Use querySelectorAll to handle multiple elements with same field name
      allErrorFields.forEach(errorField => {
        const errorElements = document.querySelectorAll(
          `[data-field-name="${errorField.name}"]`
        );
        errorElements.forEach(errorElement => {
          removeExistingErrorMessage(errorElement);
          addRequiredErrorMessage(errorElement);
        });
      });

      // Additionally, handle section-level LDAP validation (Primary Contacts)
      try {
        const ldapFieldsInRequiredSections = formFields.filter(
          f => f.type === 'ldap' && (f.sectionConfig as any)?.required
        );
        const anySelected = ldapFieldsInRequiredSections.some(
          f =>
            Array.isArray(formValues[f.name]) && formValues[f.name]?.length > 0
        );
        if (!anySelected && ldapFieldsInRequiredSections.length > 0) {
          ldapFieldsInRequiredSections.forEach(f => {
            const el = document.querySelector(`[data-field-name="${f.name}"]`);
            if (el) {
              removeExistingErrorMessage(el);
              addRequiredErrorMessage(el);
            }
          });
        }
      } catch (e) {
        // ignore
      }

      // Scroll to the FIRST error field for better UX
      // Find the first field with an error (either from allErrorFields or from DOM error messages)
      let firstErrorElement: Element | null = null;

      // Special case: Check if only Internal Beta is selected (priority scroll target)
      const hasInternalBeta = isPhaseDatePresent(
        formValues?.[INTERNAL_BETA_PHASE]?.phaseDate
      );
      const phaseGroups = formFields.filter(
        f =>
          f.type === 'group_component' &&
          Array.isArray((f as any).fieldGroup) &&
          (f as any).fieldGroup.some((gf: any) => gf.name === 'phaseDate')
      );
      const phaseGroupNames = phaseGroups.map(pg => pg.name);
      const otherPhasesPresent = PHASES_WITHOUT_INTERNAL_BETA.filter(
        phaseName => phaseGroupNames.includes(phaseName)
      ).filter(phaseName =>
        isPhaseDatePresent(formValues?.[phaseName]?.phaseDate)
      );

      const onlyInternalBetaSelected =
        hasInternalBeta && otherPhasesPresent.length === 0;

      // If only Internal Beta is selected, scroll to Internal Beta phase
      if (onlyInternalBetaSelected) {
        firstErrorElement = document.querySelector(
          `[data-field-name="${INTERNAL_BETA_PHASE}"]`
        );
      }

      if (!firstErrorElement && allErrorFields.length > 0) {
        firstErrorElement = document.querySelector(
          `[data-field-name="${allErrorFields[0].name}"]`
        );
      }

      // Also check for fields with DOM error messages (like effectiveDate, marketingDate, phases)
      if (!firstErrorElement) {
        const allErrorMessages = document.querySelectorAll('.error-message');
        if (allErrorMessages.length > 0) {
          // Find the parent field element of the first error message
          firstErrorElement = (allErrorMessages[0] as HTMLElement).closest(
            '[data-field-name]'
          );
        }
      }

      if (firstErrorElement) {
        // Find the scrollable parent container dynamically
        const findScrollableParent = (element: Element): Element | null => {
          let parent = element.parentElement;
          while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            if (
              (overflowY === 'auto' || overflowY === 'scroll') &&
              parent.scrollHeight > parent.clientHeight
            ) {
              return parent;
            }
            parent = parent.parentElement;
          }
          return null;
        };

        const scrollContainer = findScrollableParent(firstErrorElement);

        if (scrollContainer) {
          // Calculate position and scroll within the container
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = firstErrorElement.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;
          const targetScroll = scrollContainer.scrollTop + relativeTop - 80;

          scrollContainer.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
        } else {
          // Fallback: use scrollIntoView with proper alignment
          firstErrorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }

        // Focus the input after scroll completes
        setTimeout(() => {
          const inputElement = firstErrorElement!.querySelector(
            'input, select, textarea'
          );
          if (inputElement) {
            (inputElement as HTMLElement).focus();
          }
        }, 500);
      }
      return;
    }

    if (isFormValid && activeMomentType) {
      // Ensure hidden fields are included in payload with safe defaults
      const withHiddenDefaults = { ...formValues };
      try {
        const hiddenFields = formFields.filter(
          (f: any) => (f as any)?.showField === false
        );
        const getDefaultValueForType = (type: string) => {
          switch (type) {
            case 'multi_select_richtext':
              return { options: [], content: '' };
            case 'dropdown_richtext':
              return { option: '', content: '' };
            case 'multi_select':
            case 'multi_select_api':
              return [];
            case 'richtext':
              return '';
            case 'select':
            case 'api_dropdown':
            case 'text':
            case 'datepicker':
            default:
              return '';
          }
        };

        hiddenFields.forEach((f: any) => {
          const name = f.name;
          if (withHiddenDefaults[name] === undefined) {
            withHiddenDefaults[name] = getDefaultValueForType(f.type);
          }
        });
      } catch (e) {
        // non-blocking safeguard
      }

      const { title, momentTypeId, ...rest } = withHiddenDefaults;

      // Filter out internal fields and convert audienceStrategyMappings to grouped structure
      const cleanedRest: Record<string, any> = {};
      Object.entries(rest).forEach(([key, value]) => {
        // Skip internal mapping fields
        if (key.endsWith('_categoryMap') || key.endsWith('_categories')) {
          return;
        }

        // Convert audienceStrategyMappings from array to grouped structure
        if (key === 'audienceStrategyMappings' && Array.isArray(value)) {
          const categoryMap = rest['audienceStrategyMappings_categoryMap'];
          const allCategories =
            rest['audienceStrategyMappings_categories'] || [];

          if (categoryMap) {
            // Initialize all categories with empty arrays
            const grouped: Record<string, string[]> = {};
            allCategories.forEach((category: string) => {
              grouped[category] = [];
            });

            // Populate with selected values
            value.forEach((val: string) => {
              const category = categoryMap[val];
              if (category && grouped[category]) {
                grouped[category].push(val);
              }
            });
            cleanedRest[key] = grouped;
          } else {
            cleanedRest[key] = value;
          }
        } else {
          cleanedRest[key] = value;
        }
      });

      const momentData: MomentData = {
        ...cleanedRest
      };
      if (title) {
        momentData.name = title;
      }

      // Extract showOnRoadmap from releaseDateInfo custom component if it exists
      if (
        momentData.releaseDateInfo &&
        typeof momentData.releaseDateInfo === 'object'
      ) {
        if (momentData.releaseDateInfo.showOnRoadmap !== undefined) {
          momentData.showOnRoadmap = momentData.releaseDateInfo.showOnRoadmap;
        }
        // Keep effectiveDate at root level if it exists in releaseDateInfo
        if (momentData.releaseDateInfo.effectiveDate !== undefined) {
          momentData.effectiveDate = momentData.releaseDateInfo.effectiveDate;
        }
      }

      // Transform goal field from string to object format for API compatibility
      if (momentData.goal && typeof momentData.goal === 'string') {
        momentData.goal = {
          option: momentData.goal,
          content: ''
        };
      }

      // Transform rtmPhases from component format to API format
      // Component: { routeToMarket, customerNotificationDate, effectiveDate }
      // API:       { name, generalInfo: { customerNotificationDate, effectiveDate? } }
      // Omit effectiveDate when empty — backend rejects generalInfo.effectiveDate: "".
      if (Array.isArray(momentData.rtmPhases)) {
        momentData.rtmPhases = momentData.rtmPhases
          .filter((phase: any) => phase != null)
          .map((phase: any) => {
            const generalInfo: Record<string, any> = {
              customerNotificationDate: phase.customerNotificationDate
            };
            if (isPhaseDatePresent(phase.effectiveDate)) {
              generalInfo.effectiveDate = phase.effectiveDate;
            }
            return {
              name: phase.routeToMarket,
              generalInfo
            };
          });
      }

      const submittedMomentType =
        (momentData.momentType as string | undefined) ??
        activeMomentType ??
        undefined;
      if (submittedMomentType === END_OF_LIFE_MOMENT_TYPE) {
        omitEolDisallowedPayloadKeys(momentData);
        applyEolStagePayloadKeyNames(momentData, eolStageGroups);
      }

      setIsSubmitting(true);
      try {
        const response = await api.createMoment(momentData as any);

        setIsSubmitting(false);

        // Clear both Events and Marketing Moments caches after successful creation
        // IMPORTANT: This must happen BEFORE any callbacks to ensure fresh data
        // Guard against multiple calls within the same session
        if (!cacheCleared) {
          try {
            clearEventsCache();
            clearParentMomentsCache();
            setCacheCleared(true); // Mark as cleared to prevent duplicate clearing
          } catch (cacheError) {
            console.error(
              '❌ [CreateMoment] Cache clearing failed (non-blocking):',
              cacheError
            );
            // Continue with success flow even if cache clearing fails
          }
        }

        // Extract issueId from response for slideout navigation
        const responseIssueId =
          (response as any)?.issueId || (response as any)?.id;

        // On success, call onSuccess callback and open slideout
        const successMessage =
          typeof response === 'string'
            ? response
            : (response as any)?.message ||
              `${momentTypeName} created successfully!`;

        if (onSuccess) {
          onSuccess(successMessage);
        }

        // Close the dialog
        onClose();

        // Open slideout for the newly created moment (only for certain moment types)
        if (responseIssueId) {
          // Moment types that should NOT open slideout: key dates and events only
          const noSlideoutMomentTypes = [
            'key-date',
            'event',
            'Key Date',
            'Event'
          ];

          const shouldOpenSlideout = !noSlideoutMomentTypes.includes(
            activeMomentType || ''
          );

          if (shouldOpenSlideout) {
            openSlideout(responseIssueId);
          } else {
            // Show success message instead of opening slideout
            if (!onSuccess) {
              setAlert({
                message: successMessage,
                variant: 'informative'
              });
            }
          }
        } else {
          console.warn(
            '⚠️ No issueId in response, cannot open slideout:',
            response
          );
          // Fallback to showing alert if no slideout can be opened
          if (!onSuccess) {
            setAlert({
              message: successMessage,
              variant: 'informative'
            });
          }
        }
      } catch (error: any) {
        setIsSubmitting(false);
        setSubmitError(error?.message || 'Failed to submit.');

        setAlert({
          message: error?.message || 'Failed to submit.',
          variant: 'error'
        });

        // Scroll to top of form if error status is 404

        const formContainer = document.querySelector(
          `.${styles.formContainer}`
        );
        if (formContainer) {
          formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  // Helper function to check if rich text content is truly empty (strips HTML tags)
  const isRichTextContentEmpty = (content: string): boolean => {
    if (!content || content === '' || content.trim() === '') {
      return true;
    }
    // Check if it's just empty HTML tags (like <p><br></p> from Quill)
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  };

  // Helper function to check if a field is empty based on its type
  const isFieldEmpty = (
    field: any,
    value: any,
    allFormValues: Record<string, any> = formValues
  ): boolean => {
    // Handle null, undefined first
    if (value === null || value === undefined) {
      return true;
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'url':
      case 'password':
      case 'select':
      case 'api_dropdown':
      case 'datepicker':
      case 'date':
        return (
          value === '' || (typeof value === 'string' && value.trim() === '')
        );

      case 'daterangepicker':
        // Check if it's an empty string or null
        if (value === '' || value === null) {
          return true;
        }
        // Check if it's an object with empty start/end
        if (typeof value === 'object') {
          // Handle {type: 'date', start: '', end: ''} format
          if (value.type === 'date') {
            return (
              !value.start ||
              !value.end ||
              value.start === '' ||
              value.end === ''
            );
          }
          // Handle {type: 'months', months: []} format
          if (value.type === 'months') {
            return (
              !value.months ||
              !Array.isArray(value.months) ||
              value.months.length === 0
            );
          }
          // Handle {start: '', end: ''} format
          return (
            !value.start || !value.end || value.start === '' || value.end === ''
          );
        }
        return false;

      case 'multi_select':
      case 'multi_select_api':
        return !Array.isArray(value) || value.length === 0;

      case 'multi_select_richtext':
        // Value structure: { options: string[], content: string }
        if (!value || typeof value !== 'object') {
          return true;
        }

        // Check if options array is empty
        if (!Array.isArray(value.options) || value.options.length === 0) {
          return true;
        }

        // Also check if content is truly empty (strip HTML tags)
        if (
          typeof value.content === 'string' &&
          isRichTextContentEmpty(value.content)
        ) {
          return true;
        }

        return false;

      case 'ldap':
        // Check if this is one of the 3 manager LDAP fields

        let isLdapFieldEmpty = !Array.isArray(value) || value.length === 0;

        // If this is a manager LDAP field, check if ANY of the 3 manager fields has values

        return isLdapFieldEmpty;

      case 'richtext':
        if (
          value === '' ||
          (typeof value === 'string' && value.trim() === '')
        ) {
          return true;
        }
        // Check if it's just empty HTML tags
        if (typeof value === 'string') {
          return isRichTextContentEmpty(value);
        }
        return false;

      case 'repeatable':
        const vals = value || [];
        return (
          !Array.isArray(vals) ||
          vals.length < (field.minItems || 1) ||
          vals.some((v: any) => v === undefined || v === '')
        );

      case 'dropdown_richtext':
        if (typeof value === 'object' && value !== null) {
          // Check if dropdown option is not selected
          if (!value.option || value.option === '') {
            return true;
          }

          // Check if content is truly empty (strip HTML tags like Quill's <p><br></p>)
          if (!value.content || typeof value.content === 'string') {
            return isRichTextContentEmpty(value.content || '');
          }

          return false;
        }
        return true;

      case 'custom_component':
        // For custom components, check common empty states
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return (
          value === '' || (typeof value === 'string' && value.trim() === '')
        );

      case 'repeatable_group_component':
        if (!Array.isArray(value) || value.length === 0) return true;
        return !value.some((phase: any) => phase && phase.routeToMarket);

      case 'switch':
      case 'checkbox':
        // For boolean fields, false can be a valid value, only undefined/null is empty
        return value === undefined || value === null;

      case 'group_component':
        // Release phases / EOL stages validate via validatePhases and validateMinOneStage — not blanket required()
        return false;

      default:
        // Default case for any other field types
        return (
          value === '' || (typeof value === 'string' && value.trim() === '')
        );
    }
  };

  const validateForm = (values = formValues) => {
    const requiredFields = formFields.filter(
      field =>
        field.required &&
        !(field.type === 'ldap' && (field.sectionConfig as any)?.required)
    );
    const errors: Record<string, string> = {};
    let isValid = true;

    // Treat fields with missing dependencies as disabled and skip validation for them
    const isDependencyMissing = (field: any, vals: Record<string, any>) => {
      if (!field?.dependsOn) return false;
      const deps = Array.isArray(field.dependsOn)
        ? field.dependsOn
        : [field.dependsOn];
      const isEmpty = (v: any) =>
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      return deps.some(dep => isEmpty(vals[dep]));
    };

    // Helper to check if ANY tier (top-level or phase-level) is L1 or L2
    const hasL1orL2Tier = (vals: Record<string, any>): boolean => {
      // Check top-level tier field (for moment types like "Marketing Moment")
      const topLevelTier = vals.tier;
      if (topLevelTier === 'L1' || topLevelTier === 'L2') {
        return true;
      }

      // Check phase-level tier fields
      return PHASE_FIELDS.some(phaseName => {
        const phaseData = vals[phaseName];
        if (phaseData && typeof phaseData === 'object') {
          const tier = phaseData.tier;
          return tier === 'L1' || tier === 'L2';
        }
        return false;
      });
    };

    requiredFields.forEach(field => {
      if (isDependencyMissing(field, values)) {
        return; // skip disabled fields
      }

      // Special handling for audienceStrategyMappings - only required if L1/L2 tier exists (EOL exempt)
      if (field.name === 'audienceStrategyMappings') {
        if (
          activeMomentType === END_OF_LIFE_MOMENT_TYPE ||
          !hasL1orL2Tier(values)
        ) {
          return;
        }
      }

      // EOL create form omits goal even if the server template still lists it pre-migration
      if (
        activeMomentType === END_OF_LIFE_MOMENT_TYPE &&
        (field.name === 'goal' || field.id === 'goal')
      ) {
        return;
      }

      if (isFieldEmpty(field, values[field.name], values)) {
        errors[field.name] = `${field.label} is required.`;
        isValid = false;
      }
    });

    // Section-level validation for Primary Contacts (or any section marked required with ldap fields)
    try {
      // Build a map of section name -> fields
      const sectionToFields = new Map<string, any[]>();
      formFields.forEach(f => {
        const key = f.section || '';
        if (!sectionToFields.has(key)) sectionToFields.set(key, []);
        sectionToFields.get(key)!.push(f);
      });

      // Check each section: if sectionConfig.required and contains ldap fields -> require at least one filled
      sectionToFields.forEach((sFields, sectionName) => {
        const hasLdap = sFields.some(f => f.type === 'ldap');
        const requiredSection = Boolean(sFields[0]?.sectionConfig?.required);
        if (requiredSection && hasLdap) {
          const anySelected = sFields.some(
            f => Array.isArray(values[f.name]) && values[f.name]?.length > 0
          );
          if (!anySelected) {
            // Mark each ldap field as error for DOM display
            sFields
              .filter(f => f.type === 'ldap')
              .forEach(f => {
                errors[f.name] = `${f.label} is required.`;
              });
            isValid = false;
          } else {
            // Clear errors for ldap fields in this section if any selected
            sFields
              .filter(f => f.type === 'ldap')
              .forEach(f => {
                delete errors[f.name];
              });
          }
        }
      });
    } catch (e) {
      // non-blocking
    }

    try {
      if (eolStageGroups.length > 0) {
        if (!validateMinOneStage(values, isPhaseDatePresent).isValid) {
          isValid = false;
        }
      }
    } catch (e) {
      // non-blocking
    }

    // After first submit, keep DOM error messages in sync on every validation run
    // Use querySelectorAll to handle multiple elements with same field name
    try {
      if (hasSubmitted) {
        requiredFields.forEach(field => {
          const fieldElements = document.querySelectorAll(
            `[data-field-name="${field.name}"]`
          );
          if (fieldElements.length === 0) return;

          fieldElements.forEach(fieldEl => {
            // If disabled by missing dependency, ensure message is removed
            if (isDependencyMissing(field, values)) {
              removeExistingErrorMessage(fieldEl);
              return;
            }
            // Special handling for audienceStrategyMappings - skip if EOL or no L1/L2 tier
            if (field.name === 'audienceStrategyMappings') {
              if (
                activeMomentType === END_OF_LIFE_MOMENT_TYPE ||
                !hasL1orL2Tier(values)
              ) {
                removeExistingErrorMessage(fieldEl);
                return;
              }
            }
            // Sync message presence based on current errors
            removeExistingErrorMessage(fieldEl);
            if (errors[field.name]) {
              addRequiredErrorMessage(fieldEl);
            }
          });
        });

        if (activeMomentType === END_OF_LIFE_MOMENT_TYPE) {
          syncEolStageDomErrors(
            values as Record<string, unknown>,
            true,
            isPhaseDatePresent,
            eolStageDomHelpers
          );
        }
      }
    } catch (e) {
      // non-blocking
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Keep DOM "Required Field" labels in sync on every render after submit
  useEffect(() => {
    if (!hasSubmitted) return;
    try {
      const isEmpty = (v: any) =>
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      const isDependencyMissing = (field: any) => {
        if (!field?.dependsOn) return false;
        const deps = Array.isArray(field.dependsOn)
          ? field.dependsOn
          : [field.dependsOn];
        return deps.some((dep: string) => isEmpty(formValues[dep]));
      };

      // Helper to check if ANY tier (top-level or phase-level) is L1 or L2
      const hasL1orL2Tier = (): boolean => {
        // Check top-level tier field (for moment types like "Marketing Moment")
        const topLevelTier = formValues.tier;
        if (topLevelTier === 'L1' || topLevelTier === 'L2') {
          return true;
        }

        // Check phase-level tier fields
        return PHASE_FIELDS.some(phaseName => {
          const phaseData = formValues[phaseName];
          if (phaseData && typeof phaseData === 'object') {
            const tier = phaseData.tier;
            return tier === 'L1' || tier === 'L2';
          }
          return false;
        });
      };

      const requiredFields = formFields.filter(
        f =>
          f.required &&
          !(f.type === 'ldap' && (f.sectionConfig as any)?.required)
      );

      // Use querySelectorAll to handle multiple elements with same field name
      requiredFields.forEach(field => {
        const elements = document.querySelectorAll(
          `[data-field-name="${field.name}"]`
        );
        if (elements.length === 0) return;

        elements.forEach(el => {
          // Special handling for audienceStrategyMappings - skip if EOL or no L1/L2 tier
          if (field.name === 'audienceStrategyMappings') {
            if (
              activeMomentType === END_OF_LIFE_MOMENT_TYPE ||
              !hasL1orL2Tier()
            ) {
              removeExistingErrorMessage(el);
              return;
            }
          }

          // Remove if disabled or now valid
          if (
            isDependencyMissing(field) ||
            !isFieldEmpty(field, formValues[field.name], formValues)
          ) {
            removeExistingErrorMessage(el);
          } else {
            // Ensure present when invalid
            const existing = (el as Element).querySelector('.error-message');
            if (!existing) addRequiredErrorMessage(el as Element);
          }
        });
      });

      if (activeMomentType === END_OF_LIFE_MOMENT_TYPE) {
        syncEolStageDomErrors(
          formValues as Record<string, unknown>,
          true,
          isPhaseDatePresent,
          eolStageDomHelpers
        );
      }
    } catch (e) {
      // non-blocking
    }
  }, [
    hasSubmitted,
    formValues,
    formFields,
    activeMomentType,
    eolStageGroups,
    syncEolStageDomErrors
  ]);

  // Focus on name input when form loads
  useEffect(() => {
    if (formFields.length > 0) {
      const nameField = formFields.find(field => field.label === 'NAME');
      if (nameField && !formValues[nameField.name]) {
        setTimeout(() => {
          const nameInput = document.querySelector(
            `[data-field-name="${nameField.name}"] input`
          );
          if (nameInput instanceof HTMLElement) {
            nameInput.focus();
          }
        }, 100);
      }
    }
  }, [formFields]);

  if (!activeMomentType) {
    return (
      <Flex
        UNSAFE_className={styles.formContainer}
        direction="column"
        gap="size-200"
        data-testid="select-moment-type-container"
      >
        <Heading
          UNSAFE_className={styles.formHeader}
          level={2}
          data-testid="select-moment-type-header"
        >
          Select a Moment Type
        </Heading>
        <View
          UNSAFE_className={styles.contentContainer}
          data-testid="select-moment-type-content"
        >
          <Text>Please select a moment type first</Text>
        </View>
        <View
          UNSAFE_className={styles.formActions}
          data-testid="select-moment-type-actions"
        >
          <Button
            variant="secondary"
            onPress={onClose}
            data-testid="select-moment-type-close-button"
          >
            Close
          </Button>
        </View>
      </Flex>
    );
  }

  return (
    <Flex
      UNSAFE_className={`${addStyles.container} ${
        styles.formContainer
      } ${getDialogSizeClass()}`}
      direction="column"
      gap="20px"
      data-testid="create-moment-container"
    >
      <div className={styles.headerRow}>
        <Heading
          UNSAFE_className={styles.formHeader}
          level={2}
          data-testid="create-moment-header"
        >
          Create New
        </Heading>
        <button
          type="button"
          aria-label="Close"
          className={styles.closeButton}
          onClick={onClose}
          data-testid="create-moment-close-icon"
        >
          <img src={closeIcon} alt="" width={20} height={20} />
        </button>
      </div>

      {alert && (
        <AlertBanner
          variant={alert.variant}
          message={alert.message}
          onClose={() => setAlert(null)}
          data-testid="create-moment-alert"
        />
      )}

      {loadingFormFields ? (
        <View
          UNSAFE_className={styles.loadingContainer}
          data-testid="create-moment-loading"
        >
          <ProgressCircle aria-label="Loading form" size="L" isIndeterminate />
        </View>
      ) : formFieldsError ? (
        <View
          UNSAFE_className={styles.contentContainer}
          data-testid="create-moment-error"
        >
          <Text>Failed to load form: {formFieldsError}</Text>
          <Text
            UNSAFE_style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}
          >
            Active moment type: {activeMomentType || 'None'} | Selected moment
            type: {selectedMomentType || 'None'} | Form fields count:{' '}
            {formFields.length}
          </Text>
        </View>
      ) : activeMomentType && formFields.length > 0 ? (
        <Form
          UNSAFE_className={`${styles.rsdForm} ${styles.rsdFormContainer} ${styles.hideScrollbar}`}
          data-testid="create-moment-form"
        >
          <View
            UNSAFE_className={`${styles.contentContainer} ${styles.hideScrollbar}`}
            data-testid="create-moment-content"
          >
            <FormContent
              formFields={formFields}
              formValues={formValues}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              momentTypeId={activeMomentType}
              validationErrors={validationErrors}
              hasSubmitted={hasSubmitted}
              data-testid="create-moment-form-content"
            />
          </View>

          <View
            UNSAFE_className={styles.formActions}
            data-testid="create-moment-actions"
          >
            <Button
              UNSAFE_className={styles.formCloseButton}
              variant="secondary"
              onPress={onClose}
              data-testid="create-moment-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="accent"
              onPress={handleSubmit}
              data-testid="create-moment-submit-button"
              isDisabled={isSubmitting}
            >
              Submit
            </Button>
          </View>
        </Form>
      ) : (
        <View
          UNSAFE_className={styles.contentContainer}
          data-testid="create-moment-debug"
        >
          <Text>Form not ready to display</Text>
          <Text
            UNSAFE_style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}
          >
            Active moment type: {activeMomentType || 'None'} | Selected moment
            type: {selectedMomentType || 'None'} | Form fields count:{' '}
            {formFields.length} | Loading: {loadingFormFields ? 'Yes' : 'No'}
          </Text>
        </View>
      )}
    </Flex>
  );
};
